import { useState } from 'react';
import { ShoppingCart, ChevronDown, ChevronUp } from 'lucide-react';
import { ProductVariation, BulkOrderTableProps } from '../types';
import { Button, QuantitySelector } from './ui';

function getTextColor(hexColor: string): string {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}

export function BulkOrderTable({ product, onAddToCart, disabled = false }: BulkOrderTableProps) {
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [isOpen, setIsOpen] = useState(false);

  const handleQuantityChange = (variationId: string, value: number) => {
    setQuantities(prev => ({
      ...prev,
      [variationId]: value
    }));
  };

  const handleAddAll = () => {
    const items = Object.entries(quantities)
      .filter(([, qty]) => qty > 0)
      .map(([varId, qty]) => {
        const variation = product.variations.find(v => String(v.id) === varId);
        return variation ? { variation, quantity: qty } : null;
      })
      .filter((item): item is { variation: ProductVariation; quantity: number } => item !== null);

    if (items.length > 0) {
      onAddToCart(items);
      setQuantities({});
      setIsOpen(false);
    }
  };

  const totalItems = Object.values(quantities).reduce((sum, qty) => sum + qty, 0);

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 bg-gray-50 flex items-center justify-between hover:bg-gray-100 transition-colors"
      >
        <div className="text-left">
          <h3 className="text-lg font-semibold text-gray-900">Bulk Order</h3>
          <p className="text-sm text-gray-600 mt-1">Select quantities for each variation</p>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-gray-500" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-500" />
        )}
      </button>

      {isOpen && (
        <div className="border-t border-gray-200">

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Size
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Color
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                SKU
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Price
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Stock
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Quantity
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {product.variations.map((variation) => {
              const bgColor = variation.hex || '#E5E7EB';
              const textColor = getTextColor(bgColor);
              const isOnDemand = Boolean(product.allowOnDemand) && variation.stock <= 0;
              const maxQuantity = isOnDemand ? 999 : Math.min(variation.stock, 999);
              const variationIdStr = String(variation.id);
              const currentQty = quantities[variationIdStr] || 0;
              const stockLabel = variation.stock > 0
                ? `${variation.stock} available`
                : 'Out of stock';
              const stockClass = variation.stock > 0
                ? 'text-green-600'
                : 'text-red-600';

              return (
                <tr key={variation.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {variation.size}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium"
                      style={{
                        backgroundColor: bgColor,
                        color: textColor
                      }}
                    >
                      {variation.color}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {variation.sku || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                    ${variation.price}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {!isOnDemand ? (
                      <span className={`${stockClass} font-medium`}>
                        {stockLabel}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <QuantitySelector
                      quantity={currentQty}
                      onQuantityChange={(newQty) => handleQuantityChange(variationIdStr, newQty)}
                      min={0}
                      max={maxQuantity}
                      size="sm"
                      stock={variation.stock}
                      showStock={false}
                      allowManualInput={true}
                      allowOnDemand={product.allowOnDemand}
                      disabled={variation.stock <= 0 && !isOnDemand}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Total items: <span className="font-semibold text-gray-900">{totalItems}</span>
            </div>
            <Button
              onClick={handleAddAll}
              disabled={disabled || totalItems === 0}
              variant="primary"
              size="md"
              icon={<ShoppingCart className="w-5 h-5" />}
            >
              {disabled ? 'Store Closed' : `Add ${totalItems > 0 ? `${totalItems} item${totalItems !== 1 ? 's' : ''}` : 'items'} to Cart`}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
